import { Menu, Locate } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { AddressSearch } from '@/components/Map/AddressSearch'
import type { BrandRecord } from '@/types'
import { BrandIdentity } from './BrandIdentity'
import parkezeMark from '@/assets/parkeze-mark.png'

type MapStyleType = 'map' | 'satellite'

interface HeaderProps {
  onMenuToggle?: () => void
  onLocateMe?: () => void
  onLocationSearch?: (lat: number, lng: number, placeName: string) => void
  availableCount?: number
  occupiedCount?: number
  mapType?: MapStyleType
  onMapTypeChange?: (type: MapStyleType) => void
  workerUrl?: string
  userLocation?: { latitude: number; longitude: number } | null
  /** The operator's brand record on /u/<alias>; null on Parkeze's own map. */
  brand?: BrandRecord | null
}

export function Header({ 
  onMenuToggle, 
  onLocateMe,
  onLocationSearch,
  availableCount = 0,
  occupiedCount = 0,
  mapType = 'map',
  onMapTypeChange,
  workerUrl = '',
  userLocation,
  brand,
}: HeaderProps) {

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 border-b shadow-sm ${
        brand
          ? 'bg-background border-border'
          : 'bg-background/95 backdrop-blur-md border-border'
      }`}
      style={{
        paddingTop: 'env(safe-area-inset-top)',
      }}
    >
      <div className="px-3 sm:px-4">
        {/* Desktop Layout */}
        <div className="hidden md:grid md:grid-cols-3 items-center gap-4 h-14">
          {/* Left: Logo */}
          <div className="flex items-center min-w-0">
            {brand ? (
              <BrandIdentity brand={brand} height={40} maxWidth={220} nameClassName="text-lg" />
            ) : (
              <a href="https://parkeze.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
                <img 
                  src={parkezeMark} 
                  alt="Parkeze" 
                  className="h-7 w-auto"
                />
                <span className="font-display font-bold text-xl tracking-tight">Parkeze</span>
              </a>
            )}
          </div>
          
          {/* Center: Search Bar */}
          <div className="flex justify-center">
            {onLocationSearch && workerUrl && (
              <div className="w-full max-w-xl">
                <AddressSearch 
                  onLocationSelect={onLocationSearch}
                  workerUrl={workerUrl}
                  userLocation={userLocation}
                />
              </div>
            )}
          </div>
          
          {/* Right: Controls */}
          <div className="flex items-center justify-end gap-2">
            {/* Sensor Count Badge */}
            <div className="flex items-center gap-3 px-3 py-1.5 bg-muted rounded-md text-sm">
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                <span className="font-medium">{availableCount}</span>
              </div>
              <div className="w-px h-4 bg-border" />
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-red-500" />
                <span className="font-medium">{occupiedCount}</span>
              </div>
            </div>
            
            {/* Locate Me Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={onLocateMe}
              className="h-9 w-9 p-0"
              title="Find my location"
            >
              <Locate size={16} />
            </Button>
            
            {/* Map Type Toggle */}
            <div className="flex items-center bg-muted rounded-md p-0.5 gap-0.5">
              <button
                onClick={() => onMapTypeChange?.('map')}
                className={`px-2 py-1.5 text-xs rounded transition-all ${
                  mapType === 'map' 
                    ? 'bg-background shadow-sm text-foreground font-medium' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Map
              </button>
              <button
                onClick={() => onMapTypeChange?.('satellite')}
                className={`px-2 py-1.5 text-xs rounded transition-all ${
                  mapType === 'satellite' 
                    ? 'bg-background shadow-sm text-foreground font-medium' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Satellite
              </button>
            </div>
            
            {/* Menu Button */}
            <Button
              variant="default"
              size="sm"
              onClick={onMenuToggle}
              className="h-9"
            >
              <Menu size={16} className="mr-1.5" />
              Menu
            </Button>
          </div>
        </div>
        
        {/* Mobile Layout */}
        <div className="md:hidden">
          <div className="flex items-center justify-between h-12">
            {/* Logo */}
            {brand ? (
              <BrandIdentity brand={brand} height={30} maxWidth={150} nameClassName="text-base" />
            ) : (
              <a href="https://parkeze.com" target="_blank" rel="noopener noreferrer" className="flex items-center hover:opacity-80 transition-opacity">
                <img 
                  src={parkezeMark} 
                  alt="Parkeze" 
                  className="h-6 w-auto"
                />
              </a>
            )}
            
            {/* Mobile Controls */}
            <div className="flex items-center gap-1">
              {/* Mobile Sensor Count Badge */}
              <div className="flex items-center gap-2 px-2 py-1.5 bg-muted/80 rounded-lg text-xs mr-1">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  <span className="font-semibold">{availableCount}</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-red-500" />
                  <span className="font-semibold">{occupiedCount}</span>
                </div>
              </div>
              
              <button
                onClick={onLocateMe}
                className="p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center text-muted-foreground hover:text-foreground active:bg-muted/50 rounded-lg transition-colors tap-highlight"
                title="Find my location"
              >
                <Locate size={20} />
              </button>
              <button
                onClick={onMenuToggle}
                className="p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center bg-primary text-primary-foreground rounded-lg transition-colors"
                title="View zones"
              >
                <Menu size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  )
}
